import { ES } from "./locales/es";
import { FR } from "./locales/fr";
import { JA } from "./locales/ja";
import { EO } from "./locales/eo";
import { QC } from "./locales/qc";
import { BR } from "./locales/br";
import { CR } from "./locales/cr";
import { CL } from "./locales/cl";
import { SCO } from "./locales/sco";
import { MP } from "./locales/mp";

// the english text is the key, so anything nobody has translated yet just shows in english.
// a catalogue only lists what reads differently, {name} bits are filled in by translate()

export type Catalogue = Record<string, string>;

export interface Language {
  id: string;
  // how the language calls itself, for the picker
  label: string;
  // how chi's instructions name it
  english: string;
  // for dates and numbers
  locale: string;
  // extra rules for chi's replies, for languages that need more than a name
  note?: string;
}

export const DEFAULT_LANGUAGE = "en";

export const LANGUAGES: Language[] = [
  { id: "en", label: "English", english: "English", locale: "en-GB" },
  { id: "es", label: "Español", english: "Spanish", locale: "es-ES" },
  { id: "fr", label: "Français", english: "French", locale: "fr-FR" },
  { id: "ja", label: "日本語", english: "Japanese", locale: "ja-JP" },
  { id: "eo", label: "Esperanto", english: "Esperanto", locale: "eo" },
  { id: "qc", label: "Québécois", english: "Québécois French (Quebec French, with everyday Quebec vocabulary)", locale: "fr-CA" },
  { id: "br", label: "Brezhoneg", english: "Breton (brezhoneg)", locale: "br" },
  { id: "cr", label: "Tico (Costa Rica)", english: "Costa Rican urban slang Spanish (Tico, with voseo)", locale: "es-CR" },
  { id: "cl", label: "Chileno", english: "Chilean slang Spanish (chilenismos)", locale: "es-CL" },
  { id: "sco", label: "Scottish English", english: "Scottish English (Scots-flavoured, wee, aye, nae)", locale: "en-GB" },
  {
    id: "mp",
    label: "Minipi",
    english: "Minipi, a constructed language",
    locale: "en-GB",
    note: "Minipi words are built from p plus a vowel, with diacritics (pípȳpý). A word break is usually heard in vocalization. Sentences go verb + noun + complements + (subject), the subject often implied. A word's meaning changes with its place in the sentence. Known words: pìpý = to go and come back with, to alert someone; pȳpípí = clean water, or as a verb to wash, to remove dirt from something; pīpýpīpỳ = groundwater; pīpī = you. Example: Pìpý pȳpípí pīpýpīpỳ (pīpī) = go fetch the water from the well. Add a short plain English gloss in brackets after each Minipi reply so it can be read."
  }
];

const CATALOGUES: Record<string, Catalogue> = { es: ES, fr: FR, ja: JA, eo: EO, qc: QC, br: BR, cr: CR, cl: CL, sco: SCO, mp: MP };

export function languageById(id: string | undefined): Language {
  return LANGUAGES.find((language) => language.id === id) || LANGUAGES[0];
}

export function catalogue(id: string): Catalogue | undefined {
  return CATALOGUES[id];
}

export function translate(id: string, text: string, vars?: Record<string, string | number>): string {
  const out = CATALOGUES[id]?.[text] ?? text;
  if (!vars) return out;
  return out.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name]) : whole));
}
